import type { ContributingHazard } from '@tsr/shared-types';
import { RESPONSE_LEVEL_COLORS, isResponseLevel } from '../styles/responseLevel';
import { hazardStyle } from '../styles/incidentStyles';

/**
 * #427c — the "Contributing hazards" drill-in chip vocabulary (single source, so the
 * vocab lock has one surface to scan). A chip is hazard TYPE + a contributing SEVERITY.
 *
 * VOCAB LOCK (gate-mandated): NO chip string may contain "Response Level" or "\bRL\b".
 * There is exactly ONE Response Level per incident — the header max-fold — and these are
 * NOT it. Severity 1..4 reuses the LOCKED Response Level PALETTE for the dot colour only
 * (never re-tuned here); the operator-facing word is "severity", never "Response Level".
 * See contributingHazards.lock.test.ts.
 */

/** The neutral dot colour for a hazard that carries no numeric severity (the honest gap:
 *  arcgis features, a non-OC fire). Deliberately a muted slate OUTSIDE the 4-hue Response
 *  Level palette so it never reads as a level. Colour is never load-bearing alone (paired
 *  with the severity text + tooltip). */
export const CONTRIBUTING_HAZARD_NO_SEVERITY_COLOR = '#64748b';

/** The hazard-type label for the chip (hazardStyle: known type → doctrinal label, else
 *  the raw category text with underscores spaced). */
export function contributingHazardTypeLabel(h: ContributingHazard): string {
  return hazardStyle(h.hazard_type).label;
}

/** The hazard-type icon (emoji), paired with the label so hue is never alone. */
export function contributingHazardIcon(h: ContributingHazard): string {
  return hazardStyle(h.hazard_type).icon;
}

/** The severity DOT colour: the Response Level palette hue for a numeric 1..4 severity,
 *  else the neutral no-severity colour. */
export function contributingHazardDotColor(h: ContributingHazard): string {
  return isResponseLevel(h.severity)
    ? RESPONSE_LEVEL_COLORS[h.severity]
    : CONTRIBUTING_HAZARD_NO_SEVERITY_COLOR;
}

/** The short severity glyph on the chip: the number for a numeric severity, the native
 *  severity text where the class carries one (arcgis), else an em-dash (the honest gap).
 *  NEVER "RL"/"Response Level" (vocab lock). */
export function contributingHazardSeverityText(h: ContributingHazard): string {
  if (isResponseLevel(h.severity)) return String(h.severity);
  if (h.severity_label && h.severity_label.trim().length > 0) return h.severity_label;
  return '—';
}

/** The hover tooltip. Uses "contributing severity" — never "Response Level"/"RL". */
export function contributingHazardSeverityTooltip(h: ContributingHazard): string {
  if (isResponseLevel(h.severity)) return `contributing severity: ${h.severity} of 4`;
  if (h.severity_label && h.severity_label.trim().length > 0) {
    return `contributing severity: ${h.severity_label} (no numeric severity carried)`;
  }
  return 'no contributing severity carried';
}
